<script lang="ts" setup>
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { inject } from 'vue';
import usePendings from './usePendings';
import { solanaFactor } from '@/constants';
import { PendingInvite } from '@/types';
import { formatDate } from './formatDate';
import { twMerge } from 'tailwind-merge';
import { computed } from 'vue';
import { ref } from 'vue';
import { useWallet } from 'solana-wallets-vue';

dayjs.extend(relativeTime);
const { data, isFetching } = inject('pendings') as ReturnType<
  typeof usePendings
>;

const emit = defineEmits(['click']);

const { publicKey } = useWallet();

const tab = ref<'pending' | 'history'>('pending');

const pendingInvites = computed(() =>
  data.value?.filter((invite) => invite.action == null),
);
const historyInvites = computed(() =>
  data.value?.filter((invite) => invite.action === 'claim'),
);

const displayedInvites = computed(() =>
  tab.value === 'pending' ? pendingInvites.value : historyInvites.value,
);

const handleClick = (invite: PendingInvite) => {
  if (getStatus(invite).label != 'Pending') return;
  emit('click', invite);
};

const getStatus = (
  invite: PendingInvite,
): {
  label: string;
  color: string;
} => {
  if (invite.action === 'claim')
    return { label: 'Claimed', color: 'text-primary' };
  if (invite.action === 'clawback')
    return { label: 'Canceled', color: 'text-gray-500' };
  if (invite.action == null)
    return { label: 'Pending', color: 'text-orange-300' };
  return { label: invite.action, color: 'text-orange-200' };
};
</script>

<template>
  <div class="w-full flex flex-col gap-2" v-if="data && data.length > 0">
    <div class="flex flex-row gap-4 items-center">
      <button
        :class="twMerge('text-lg font-bold', tab === 'history' && 'opacity-50')"
        @click="tab = 'pending'"
      >
        Pending invites ({{ pendingInvites?.length ?? 0 }})
      </button>
      <button
        :class="twMerge('text-lg font-bold', tab === 'pending' && 'opacity-50')"
        @click="tab = 'history'"
      >
        Claimed ({{ historyInvites?.length ?? 0 }})
      </button>
      <v-progress-circular indeterminate size="18" v-if="isFetching" />
    </div>

    <ul class="flex flex-col gap-2">
      <li
        v-for="pendingInvite in displayedInvites"
        @click="handleClick(pendingInvite)"
        :class="
          twMerge(
            'bg-background p-4 rounded-jup flex-row flex  hover:bg-white/10',
            getStatus(pendingInvite).label === 'Pending' && 'cursor-pointer',
          )
        "
      >
        <div class="flex flex-col justify-between flex-1">
          <p class="font-bold">
            {{ formatDate(pendingInvite.creation_time) }}
          </p>
          <p
            class="text-sm text-disabled"
            v-if="getStatus(pendingInvite).label === 'Pending'"
          >
            Expire {{ dayjs(Number(pendingInvite.expiry) * 1000).fromNow() }}
          </p>
          <p v-else class="text-sm text-primary">
            Claimed
            {{
              pendingInvite.receiver === publicKey?.toString()
                ? 'by you'
                : 'by friend'
            }}
          </p>
        </div>
        <p class="font-bold self-center">
          {{ Number(pendingInvite.amount) / solanaFactor }} SOL
        </p>
      </li>
    </ul>
  </div>
  <div
    v-if="data && data.length === 0 && !isFetching"
    class="flex flex-col items-center justify-center p-4"
  >
    <p class="text-center text-2xl font-bold">You haven't invited anyone!</p>
    <p class="text-center text-disabled">
      Help your friends kickstart their Solana journey with some SOL!
    </p>
  </div>
</template>
